import { router, useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useEditSubscription } from '@/api/hooks/paymentAccountsHooks';
import { Subscription } from '@/api/models/paymentAccountsModels';
import EditSubscriptionForm from '@/components/forms/EditSubscriptionForm';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { EditSubscriptionData } from '@/lib/validations/DonationSchema';
import { decryptObject } from '@/utils/crypto';
import { clearTimezone } from '@/utils/dateFormats';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';

const EditSubscription = () => {
  const { payload } = useLocalSearchParams<{
    payload: string;
  }>();

  const { showSuccess, showError } = useEndFlowModal();

  const { mutateAsync: editSubscription, isPending: isPendingEdit } = useEditSubscription();

  const [payloadState, setPayloadState] = useState<{
    subscriptionItem?: string;
    tokenizedCard?: string;
    tokenizedACH?: string;
  }>();

  const [parsedSubscriptionItem, setParsedSubscriptionItem] = useState<Subscription>();

  useEffect(() => {
    const loadPayloadParams = async () => {
      const payloadObject = await decryptObject<{
        subscriptionItem?: string;
        tokenizedCard?: string;
        tokenizedACH?: string;
      }>(decodeURIComponent(payload));

      if (payloadObject) {
        setPayloadState(payloadObject);
        setParsedSubscriptionItem(safeJsonParse<Subscription>(payloadObject.subscriptionItem ?? ''));
      }
    };

    if (payload) {
      loadPayloadParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  const handleConfirm = (data: EditSubscriptionData) => {
    editSubscription(
      {
        idSub: parsedSubscriptionItem?.id ?? '',
        bill_amount: data.amount ?? parsedSubscriptionItem?.amount ?? '',
        due_date: data.dueDate ?? parsedSubscriptionItem?.dueDate ?? '',
        billing_period: data.giftRecurringOption ?? parsedSubscriptionItem?.billingPeriod ?? '',
        payment_method:
          payloadState?.tokenizedCard !== undefined
            ? 'CREDIT_CARD'
            : payloadState?.tokenizedACH !== undefined
              ? 'ACH'
              : parsedSubscriptionItem?.paymentType,
        key: data.key
      },
      {
        onSuccess: () => {
          showSuccess({ title: i18n.t('end_flow.success_title'), message: i18n.t('donation.subscription_edited') });
          router.back();
        },
        onError: (e) => {
          showError({ message: e.message });
          console.error(e);
        }
      }
    );
  };

  return (
    <SafeAreaView style={styles.flexOne} edges={['bottom']}>
      <View style={styles.container}>
        <EditSubscriptionForm
          initialValues={{
            amount: parsedSubscriptionItem?.amount ?? '0',
            dueDate: parsedSubscriptionItem?.dueDate ? clearTimezone(parsedSubscriptionItem.dueDate) : '',
            giftRecurringOption: parsedSubscriptionItem?.billingPeriod ?? ''
          }}
          npoId={parsedSubscriptionItem?.nonProfitOrgId ?? ''}
          onSubmitTap={handleConfirm}
          loading={isPendingEdit}
          subscriptionItem={payloadState?.subscriptionItem}
          tokenizedCard={
            payloadState?.tokenizedACH
              ? undefined
              : payloadState?.tokenizedCard !== undefined && payloadState?.tokenizedACH === undefined
                ? payloadState?.tokenizedCard
                : parsedSubscriptionItem?.paymentType === 'CREDIT_CARD'
                  ? JSON.stringify(parsedSubscriptionItem?.paymentTypeInfo)
                  : undefined
          }
          tokenizedACH={
            payloadState?.tokenizedCard
              ? undefined
              : payloadState?.tokenizedACH !== undefined && payloadState?.tokenizedCard === undefined
                ? payloadState?.tokenizedACH
                : parsedSubscriptionItem?.paymentType === 'ACH'
                  ? JSON.stringify(parsedSubscriptionItem?.paymentTypeInfo)
                  : undefined
          }
        />
      </View>
    </SafeAreaView>
  );
};

export default EditSubscription;

const styles = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  container: {
    flex: 1,
    padding: 18,
    gap: 20
  }
});
